import { useEffect, useState, Children, cloneElement } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Axios from "axios";
import GroupIcon from "@mui/icons-material/Group";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  ShoppingCart as ShoppingCartIcon,
  ReceiptLong as ReceiptLongIcon,
  Inventory2 as Inventory2Icon,
  Payments as PaymentsIcon,
  Warehouse as WarehouseIcon,
  Description as DescriptionIcon,
  Menu as MenuIcon,
  MenuBook as MenuBookIcon,
  People as PeopleIcon,
  Apartment as ApartmentIcon,
  DirectionsCar as DirectionsCarIcon,
  LocalShipping as LocalShippingIcon,
  CorporateFare as CorporateFareIcon,
  Class as ClassIcon,
  Store as StoreIcon,
  AccountBalance as AccountBalanceIcon,
  AccessTime as AccessTimeIcon,
  Map as MapIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PaymentIcon from "@mui/icons-material/Payment";
import HandymanIcon from "@mui/icons-material/Handyman";
import { APP_VERSION } from "../../config/appVersion";
import StockManagementMenu from "./Sidebar/StockManagementMenu";
import { getAlert } from "../../utils/SamuiUtils";
import {
  hasPathReadAccess,
  isPermissionEnforced,
} from "../../utils/orgAccessPermissions";

const DRAWER_WIDTH = 190;
const MINI_DRAWER_WIDTH = 72;

const HIDDEN_INITIAL_ROUTES = new Set([
  "/APtype",
  "/debtor",
  "/ARtype",
  "/dept",
  "/car",
  "/deliveryCustomerInfo",
  "/deliveryCarInfo",
]);

const SALE_NAV_PATHS = [
  "/sale",
  "/sale-customer-calendar",
  "/admin-quotelist",
  "/contractlist",
];
const JOB_NAV_PATHS = ["/jobfile", "/job-management", "/temp-equipment"];
const ACCOUNTING_NAV_PATHS = ["/invoice-record"];
const PURCHASE_NAV_PATHS = ["/purchase-order", "/product-receipt", "/payment-voucher", "/tax"];
const INITIAL_NAV_PATHS = [
  "/company",
  "/org-access",
  "/org-roles",
  "/dept",
  "/employee",
  "/team",
  "/creditor",
  "/APtype",
  "/debtor",
  "/ARtype",
  "/tbsetitem",
  "/tbsetitmgroup",
  "/tbsetwh",
  "/service",
  "/servicestep",
  "/serviceaddrtype",
  "/servicepurpose",
  "/timeunit",
  "/package",
  "/car",
  "/transType",
  "/docType",
  "/deliveryCustomerInfo",
  "/deliveryCarInfo",
  "/cover",
  "/release-notes",
  "/stock/stock-management",
];
const STOCK_NAV_PATHS = [
  "/stock/stock-overview",
  "/stock/stock-detail",
  "/stock/stock-documents",
  "/withdraw-approval",
];

export default function Sidebar() {
  const [authData, setAuthData] = useState({});
  const [hoveredSection, setHoveredSection] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === '1');
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const width = collapsed ? `${MINI_DRAWER_WIDTH}px` : `${DRAWER_WIDTH}px`;
    document.documentElement.style.setProperty('--sidebar-width', width);
    localStorage.setItem('sidebarCollapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    const p = location.pathname;
    if (p === "/login") return;
    if (!isPermissionEnforced()) return;
    if (!hasPathReadAccess(p)) {
      navigate("/", { replace: true });
      getAlert("WARNING", "ไม่มีสิทธิ์เข้าหน้านี้");
    }
  }, [location.pathname, navigate]);

  const initialize = async () => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/authen`,
        {},
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            key: process.env.REACT_APP_ANALYTICS_KEY,
          },
        }
      );
      if (response.data.status === "OK") {
        setAuthData(response.data.decoded || {});
        try {
          if (response.data.org_access) {
            localStorage.setItem('org_access', JSON.stringify(response.data.org_access));
          }
        } catch {
          /* ignore */
        }
      } else {
        window.location.replace("/login");
      }
    } catch (error) {
      window.location.replace("/login");
    }
  };

  const isActive = (paths) => {
    const list = Array.isArray(paths) ? paths : [paths];
    return list.includes(location.pathname);
  };

  const role = localStorage.getItem("role");
  const roleCode = localStorage.getItem("role_code");
  const canManagePurchaseAndInitial =
    roleCode === "1" || roleCode === "2";
  /** แฟ้มงาน / จัดการจ๊อบ / ชำระเงิน / อุปกรณ์ชั่วคราว — Admin หรือผู้จัดการ (role_code 1,2) */
  const canAccessJobCraft =
    role === "Admin" || canManagePurchaseAndInitial;

  const company = window.localStorage.getItem("company");
  const logoSrc = company === "1" ? "/logo.png" : "/logo.png";

  const showSaleReportItem =
    role === "Admin" ||
    canManagePurchaseAndInitial ||
    (isPermissionEnforced() && hasPathReadAccess("/sale-report"));
  const showSaleSection =
    !isPermissionEnforced() ||
    SALE_NAV_PATHS.some((path) => hasPathReadAccess(path)) ||
    showSaleReportItem;
  const showJobSection =
    canAccessJobCraft ||
    (isPermissionEnforced() && JOB_NAV_PATHS.some((path) => hasPathReadAccess(path)));
  const showAccountingSection =
    canAccessJobCraft ||
    (isPermissionEnforced() &&
      ACCOUNTING_NAV_PATHS.some((path) => hasPathReadAccess(path)));
  const showPurchaseSection =
    canManagePurchaseAndInitial ||
    (isPermissionEnforced() && PURCHASE_NAV_PATHS.some((path) => hasPathReadAccess(path)));
  const showInitialSection =
    canManagePurchaseAndInitial ||
    (isPermissionEnforced() && INITIAL_NAV_PATHS.some((path) => hasPathReadAccess(path)));
  const showStockSection =
    canManagePurchaseAndInitial ||
    (isPermissionEnforced() && STOCK_NAV_PATHS.some((path) => hasPathReadAccess(path)));

  const NavItem = ({ to, icon, label, forceExpanded = false }) => {
    if (HIDDEN_INITIAL_ROUTES.has(to)) {
      return null;
    }
    if (isPermissionEnforced() && !hasPathReadAccess(to)) {
      return null;
    }
    const selected = isActive(to);
    const isCollapsed = collapsed && !forceExpanded;

    const baseButtonSx = {
      borderRadius: 1.5,
      mb: isCollapsed ? 0.25 : 0.5,
      px: isCollapsed ? 0 : 1.5,
      justifyContent: isCollapsed ? 'center' : 'flex-start',
      alignItems: 'center',
      minHeight: isCollapsed ? 40 : 48,
      transition: 'background-color 0.2s ease, color 0.2s ease',
    };

    const expandedBehaviors = isCollapsed
      ? {
        '&:hover': { bgcolor: 'transparent' },
        '&.Mui-selected': { bgcolor: 'transparent', color: 'inherit' },
      }
      : {
        '&:hover': {
          bgcolor: 'rgba(225, 63, 87, 0.12)',
          color: '#E13F57',
          '& .MuiListItemIcon-root': { color: '#E13F57' },
        },
        '&.Mui-selected': {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
          '&:hover': { bgcolor: '#c42d44' },
        },
      };

    const iconSx = isCollapsed
      ? {
        minWidth: 0,
        width: 40,
        height: 40,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: selected ? 'primary.contrastText' : '#6b7280',
        bgcolor: selected ? 'primary.main' : 'grey.100',
        transition: 'background-color 0.2s ease, color 0.2s ease',
        '.MuiListItemButton-root:hover &': {
          bgcolor: selected ? 'primary.main' : 'grey.200',
          color: selected ? 'primary.contrastText' : '#E13F57',
        },
      }
      : {
        minWidth: 36,
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        color: selected ? 'primary.contrastText' : '#6b7280',
        justifyContent: 'center',
        fontSize: '1.5rem',
        '& svg': {
          fontSize: '1.5rem',
          width: '1.5rem',
          height: '1.5rem',
        },
      };

    const button = (
      <ListItemButton
        component={Link}
        to={to}
        selected={selected}
        sx={{ ...baseButtonSx, ...expandedBehaviors }}
      >
        <ListItemIcon sx={iconSx}>{icon}</ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={label}
            sx={{ my: 0, alignSelf: "center" }}
            primaryTypographyProps={{ lineHeight: 1.2, display: "block" }}
          />
        )}
      </ListItemButton>
    );
    return isCollapsed ? (
      <Tooltip title={label} placement="right">{button}</Tooltip>
    ) : (
      button
    );
  };

  const SectionHoverMenu = ({ id, icon, label, active, children }) => (
    <Tooltip title={collapsed ? label : ""} placement="right">
      <Box
        sx={{ position: "relative" }}
        onMouseEnter={() => setHoveredSection(id)}
        onMouseLeave={() => setHoveredSection((prev) => (prev === id ? null : prev))}
      >
        <ListItemButton
          sx={collapsed ? {
            borderRadius: 1.5,
            mb: 0.25,
            px: 0,
            justifyContent: 'center',
            minHeight: 40,
            '&:hover': { bgcolor: 'transparent' },
          } : {
            borderRadius: 1.5,
            mb: 0.5,
            alignItems: "center",
            bgcolor: active ? "rgba(225, 63, 87, 0.1)" : "transparent",
            "&:hover": { bgcolor: "rgba(225, 63, 87, 0.1)" },
          }}
        >
          <ListItemIcon
            sx={collapsed ? {
              minWidth: 0,
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: active ? 'primary.contrastText' : '#6b7280',
              bgcolor: active ? 'primary.main' : 'grey.100',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '.MuiListItemButton-root:hover &': {
                bgcolor: active ? 'primary.main' : 'grey.200',
                color: active ? 'primary.contrastText' : '#E13F57',
              },
            } : {
              minWidth: 36,
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              color: active ? "#E13F57" : "#6b7280",
            }}
          >
            {icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={label}
              sx={{ my: 0 }}
              primaryTypographyProps={{
                lineHeight: 1,
                color: active ? "#E13F57" : "#111827",
                fontWeight: active ? 600 : 500,
              }}
            />
          )}
        </ListItemButton>
        {hoveredSection === id && (
          <Box
            sx={{
              position: "absolute",
              left: "100%",
              top: 0,
              minWidth: 250,
              maxHeight: 420,
              overflowY: "auto",
              bgcolor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
              p: 1,
              zIndex: 3000,
            }}
          >
            {Children.map(children, (child) =>
              child ? cloneElement(child, { forceExpanded: true }) : child
            )}
          </Box>
        )}
      </Box>
    </Tooltip>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{ zIndex: 2000 }}
      PaperProps={{
        sx: {
          width: collapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH,
          border: 0,
          bgcolor: "#ffffff",
          color: "#111827",
          backgroundImage: 'none',
          borderRight: '1px solid #e5e7eb',
          transition: 'width 200ms ease',
          overflowX: 'visible',
          overflowY: 'visible',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Header / Profile */}
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: collapsed ? 56 : 80, px: 1.5 }}>
        {!collapsed && (
          <Tooltip title="เปิดโปรไฟล์">
            <IconButton onClick={() => setProfileOpen(true)} sx={{ p: 0 }}>
              <Avatar
                src={logoSrc}
                alt="Logo"
                variant="rounded"
                sx={{ width: 48, height: 48, borderRadius: 2 }}
              />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          onClick={() => setCollapsed(v => !v)}
          sx={collapsed ? {
            color: '#6b7280',
            p: 0,
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'grey.100',
            '&:hover': { bgcolor: 'grey.200' },
            '& svg': {
              fontSize: '1.5rem',
              width: '1.5rem',
              height: '1.5rem',
            },
          } : {
            color: '#6b7280',
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            '& svg': {
              fontSize: '1.5rem',
              width: '1.5rem',
              height: '1.5rem',
            },
          }}
          aria-label="toggle sidebar"
        >
          <MenuIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* Navigation */}
      <Box
        sx={{
          px: collapsed ? 0.5 : 1.25,
          py: 1,
          overflowY: 'visible',
          height: '100%',
          overflowX: "visible",
        }}
      >
        <List sx={{ pt: 0 }}>

          {/* ขาย (ลูกค้า) - move before จัดซื้อสินค้า */}
          {showSaleSection && (
            <SectionHoverMenu
              id="sale"
              icon={<GroupIcon />}
              label="งานขาย"
              active={isActive(["/sale", "/sale-customer-calendar", "/admin-quotelist", "/contractlist", "/sale-report"])}
            >
              <NavItem to="/sale" icon={<ContactPageIcon />} label="ลูกค้า" />
              <NavItem to="/sale-customer-calendar" icon={<EventAvailableIcon />} label="ปฏิทินการเข้าพบ" />
              <NavItem to="/admin-quotelist" icon={<ReceiptLongIcon />} label="ใบเสนอราคา" />
              <NavItem to="/contractlist" icon={<DescriptionIcon />} label="สัญญา" />
              {showSaleReportItem && (
                <NavItem to="/sale-report" icon={<AssessmentIcon />} label="ตรวจสอบฝ่ายขาย" />
              )}
            </SectionHoverMenu>
          )}

          {showJobSection && (
            <SectionHoverMenu
              id="jobcraft"
              icon={<GroupIcon />}
              label="งานช่าง"
              active={isActive(["/jobfile", "/job-management", "/temp-equipment"])}
            >
              <NavItem to="/jobfile" icon={<ContactPageIcon />} label="แฟ้มงาน" />
              <NavItem to="/job-management" icon={<ContactPageIcon />} label="จัดการจ๊อบ" />
              <NavItem to="/temp-equipment" icon={<HandymanIcon />} label="รายการอุปกรณ์ชั่วคราว" />
            </SectionHoverMenu>
          )}

          {showAccountingSection && (
            <SectionHoverMenu
              id="accounting"
              icon={<AccountBalanceIcon />}
              label="งานบัญชี"
              active={isActive(["/invoice-record"])}
            >
              <NavItem to="/invoice-record" icon={<PaymentIcon />} label="บันทึกการชำระเงิน" />
            </SectionHoverMenu>
          )}

          {showPurchaseSection && (
            <SectionHoverMenu
              id="purchase"
              icon={<ShoppingCartIcon />}
              label="งานจัดซื้อ"
              active={isActive(["/purchase-order", "/product-receipt", "/payment-voucher", "/tax"])}
            >
              <NavItem to="/purchase-order" icon={<ReceiptLongIcon />} label="ใบสั่งซื้อ" />
              <NavItem to="/product-receipt" icon={<Inventory2Icon />} label="ใบรับสินค้า" />
              <NavItem to="/payment-voucher" icon={<PaymentsIcon />} label="ใบสำคัญจ่าย" />
              <NavItem to="/tax" icon={<MenuBookIcon />} label="หนังสือหัก ณ ที่จ่าย" />
            </SectionHoverMenu>
          )}

          {/* คลังสินค้า - ซ่อน */}

          {/* สต๊อกสินค้า - โครงสร้างตาม foodorder สี/ฟอนต์ thunderbee */}
          <StockManagementMenu collapsed={collapsed} sectionVisible={showStockSection} />

          {showInitialSection && (
            <SectionHoverMenu
              id="initial"
              icon={<CorporateFareIcon />}
              label="ข้อมูลตั้งต้น"
              active={isActive([
                "/creditor", "/APtype", "/debtor", "/ARtype", "/company", "/org-access", "/org-roles", "/dept",
                "/employee", "/team", "/car", "/transType", "/docType", "/tbsetitem",
                "/tbsetitmgroup", "/deliveryCustomerInfo", "/deliveryCarInfo", "/service",
                "/serviceaddrtype", "/servicepurpose", "/release-notes", "/servicestep",
                "/timeunit", "/package", "/tbsetwh", "/cover",
              ])}
            >
              <NavItem to="/company" icon={<ApartmentIcon />} label="ข้อมูลบริษัท" />
              <NavItem to="/org-access" icon={<MapIcon />} label="สาขา" />
              <NavItem to="/org-roles" icon={<AdminPanelSettingsIcon />} label="สิทธิ์และบทบาท" />
              <NavItem to="/dept" icon={<CorporateFareIcon />} label="ข้อมูลแผนก" />
              <NavItem to="/employee" icon={<PeopleIcon />} label="ข้อมูลพนักงาน" />
              <NavItem to="/team" icon={<PeopleIcon />} label="ข้อมูลทีม" />
              <NavItem to="/creditor" icon={<CorporateFareIcon />} label="ข้อมูลผู้ขาย" />
              <NavItem to="/APtype" icon={<ClassIcon />} label="ข้อมูลประเภทเจ้าหนี้" />
              <NavItem to="/debtor" icon={<PeopleIcon />} label="ข้อมูลลูกหนี้" />
              <NavItem to="/ARtype" icon={<ClassIcon />} label="ข้อมูลประเภทลูกหนี้" />
              <NavItem to="/tbsetitem" icon={<StoreIcon />} label="ข้อมูลสินค้า" />
              <NavItem to="/tbsetitmgroup" icon={<ClassIcon />} label="ข้อมูลหมวดหมู่สินค้า" />
              <NavItem to="/tbsetwh" icon={<WarehouseIcon />} label="ข้อมูลคลังสินค้า" />
              <NavItem to="/service" icon={<DescriptionIcon />} label="ข้อมูลบริการ" />
              <NavItem to="/servicestep" icon={<DescriptionIcon />} label="ขั้นตอนการบริการ" />
              <NavItem to="/serviceaddrtype" icon={<ApartmentIcon />} label="ประเภทอาคาร" />
              <NavItem to="/servicepurpose" icon={<DescriptionIcon />} label="วัตถุประสงค์สถานที่บริการ" />
              <NavItem to="/timeunit" icon={<AccessTimeIcon />} label="หน่วยช่วงเวลา" />
              <NavItem to="/package" icon={<DescriptionIcon />} label="ข้อมูลแพ็คเก็จ" />
              <NavItem to="/car" icon={<DirectionsCarIcon />} label="ข้อมูลรถ" />
              <NavItem to="/transType" icon={<LocalShippingIcon />} label="ข้อมูลวิธีการขนส่ง" />
              <NavItem to="/docType" icon={<DescriptionIcon />} label="ข้อมูลประเภทเอกสาร" />
              <NavItem to="/deliveryCustomerInfo" icon={<PeopleIcon />} label="ข้อมูลลูกค้าผูกสายส่ง" />
              <NavItem to="/deliveryCarInfo" icon={<DirectionsCarIcon />} label="ข้อมูลรถผูกสายส่ง" />
              <NavItem to="/cover" icon={<DescriptionIcon />} label="ข้อมูลปกและท้าย" />
              <NavItem to="/stock/stock-management" icon={<Inventory2Icon />} label="จัดการคลังสินค้า" />
              <NavItem to="/release-notes" icon={<DescriptionIcon />} label="รายละเอียดอัปเดต" />
            </SectionHoverMenu>
          )}
        </List>
      </Box>

      {!collapsed && (
        <Box sx={{ mt: 'auto', py: 1, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Version {APP_VERSION}
          </Typography>
        </Box>
      )}

      {/* โปรไฟล์ด้วย MUI Dialog */}
      <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>ข้อมูลผู้ใช้</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar src={logoSrc} variant="rounded" sx={{ width: 72, height: 72, borderRadius: 3 }} />
            <Box>
              <Typography variant="h6">{authData.Emp_Name || "-"}</Typography>
              <Typography variant="body2" color="text.secondary">{authData.Dept_Name || ""}</Typography>
              <Typography variant="body2" color="text.secondary">{authData.PST_Name || ""}</Typography>
              <Typography variant="body2" color="text.secondary">{authData.Comp_Name_TH || ""}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" color="error" fullWidth onClick={() => window.location.replace("/login")}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
